import { initializeApp, FirebaseOptions } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';

/**
 * Récupère la configuration Firebase depuis les variables d'environnement
 */
function getFirebaseConfig(): FirebaseOptions {
  const requiredEnvVars = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  // Vérifier que toutes les variables requises sont présentes
  const missing: string[] = [];
  const envVarMap: Record<string, string> = {
    apiKey: 'VITE_FIREBASE_API_KEY',
    authDomain: 'VITE_FIREBASE_AUTH_DOMAIN',
    projectId: 'VITE_FIREBASE_PROJECT_ID',
    storageBucket: 'VITE_FIREBASE_STORAGE_BUCKET',
    messagingSenderId: 'VITE_FIREBASE_MESSAGING_SENDER_ID',
    appId: 'VITE_FIREBASE_APP_ID',
  };

  for (const [key, value] of Object.entries(requiredEnvVars)) {
    if (!value) {
      missing.push(envVarMap[key] || `VITE_FIREBASE_${key.toUpperCase()}`);
    }
  }

  if (missing.length > 0) {
    const errorMessage = `
❌ ERREUR CRITIQUE : Variables d'environnement Firebase manquantes

Variables requises manquantes:
${missing.map(v => `  - ${v}`).join('\n')}

Veuillez créer un fichier .env à la racine du projet avec:
VITE_FIREBASE_API_KEY=your-api-key
VITE_FIREBASE_AUTH_DOMAIN=your-project.firebaseapp.com
VITE_FIREBASE_PROJECT_ID=your-project-id
VITE_FIREBASE_STORAGE_BUCKET=your-project.appspot.com
VITE_FIREBASE_MESSAGING_SENDER_ID=your-sender-id
VITE_FIREBASE_APP_ID=your-app-id
VITE_FIREBASE_MEASUREMENT_ID=your-measurement-id (optionnel)
    `;
    console.error(errorMessage);
    throw new Error('Variables d\'environnement Firebase manquantes. Voir la console pour plus de détails.');
  }

  const config: FirebaseOptions = {
    apiKey: requiredEnvVars.apiKey!,
    authDomain: requiredEnvVars.authDomain!,
    projectId: requiredEnvVars.projectId!,
    storageBucket: requiredEnvVars.storageBucket!,
    messagingSenderId: requiredEnvVars.messagingSenderId!,
    appId: requiredEnvVars.appId!,
  };

  // Ajouter measurementId si disponible (optionnel)
  if (import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) {
    config.measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;
  }

  return config;
}

// Initialize Firebase avec la configuration depuis les variables d'environnement
const firebaseConfig = getFirebaseConfig();
export const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Initialize Analytics only in browser environment
const initializeAnalytics = (): ReturnType<typeof getAnalytics> | null => {
  if (globalThis.window === undefined) {
    return null;
  }

  try {
    return getAnalytics(app);
  } catch (error) {
    console.warn('Firebase Analytics initialization failed:', error);
    return null;
  }
};

export const analytics = initializeAnalytics();




